import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, ChevronRight, CheckCircle2 } from 'lucide-react';
import { StatusModal } from '../Modals/StatusModal';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { apiFetch } from '@/utils/api';

interface ActiveDealWidgetProps {
    onNavigate: (page: string) => void;
}

export const ActiveDealWidget = ({ onNavigate }: ActiveDealWidgetProps) => {
    const [deal, setDeal] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const { impactOccurred } = useHapticFeedback();

    useEffect(() => {
        // @ts-ignore
        const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
        const userId = tgUser?.id;

        if (!userId) {
            setLoading(false);
            return;
        }

        apiFetch(`/api/p2p/active?userId=${userId}`)
            .then(res => res.json())
            .then(data => {
                if (data.success && data.transaction) {
                    if (data.transaction.status === 'archived') {
                        setDeal(null);
                    } else {
                        setDeal(data.transaction);
                    }
                } else {
                    setDeal(null);
                }
            })
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    const handleCancel = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!deal?.id) return;
        setShowConfirm(true);
    };

    const confirmCancel = async () => {
        impactOccurred('heavy');
        try {
            const res = await apiFetch(`/api/p2p/${deal.id}/close`, { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                setDeal(null);
            }
        } catch (e) {
            console.error("Cancel Error:", e);
        } finally {
            setShowConfirm(false);
        }
    };

    if (loading || !deal) return null;

    const isCompleted = deal.status === 'completed';

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="px-4 mb-6"
            >
                <button
                    onClick={() => {
                        impactOccurred('medium');
                        onNavigate('p2p_transaction');
                    }}
                    className={`w-full bg-[#1c1c1e] border ${isCompleted ? 'border-green-500/30 shadow-green-900/10' : 'border-yellow-500/30 shadow-yellow-900/10'} rounded-[24px] p-4 flex items-center justify-between shadow-lg relative overflow-hidden group`}
                >
                    <div className={`absolute inset-0 bg-gradient-to-r ${isCompleted ? 'from-green-500/10' : 'from-yellow-500/10'} to-transparent opacity-50`} />

                    <div className="flex items-center gap-4 relative z-10">
                        <div className={`w-12 h-12 rounded-full ${isCompleted ? 'bg-green-500/20' : 'bg-yellow-500/20'} flex items-center justify-center ${!isCompleted && 'animate-pulse'}`}>
                            {isCompleted ? <CheckCircle2 className="w-6 h-6 text-green-500" /> : <ShieldAlert className="w-6 h-6 text-yellow-500" />}
                        </div>
                        <div className="text-left">
                            <h3 className="text-white font-bold text-sm flex items-center gap-2">
                                {isCompleted ? 'Transaction Complete' : 'Active Transaction'}
                                {!isCompleted && <span className="w-2 h-2 rounded-full bg-green-500 animate-ping" />}
                            </h3>
                            <p className="text-white/60 text-xs mt-0.5">
                                {isCompleted ? 'Deal successfully settled' : `Action Required • ${deal.status === 'verifying' ? 'Verifying...' : 'Pending Action'}`}
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2 relative z-10">
                        <div className="bg-white/10 rounded-full p-2 group-hover:bg-white/20 transition-colors">
                            <ChevronRight className="w-5 h-5 text-white/80" />
                        </div>
                    </div>
                </button>
            </motion.div>

            <StatusModal
                isOpen={showConfirm}
                onClose={() => setShowConfirm(false)}
                type="confirm"
                title={deal.status === 'completed' ? "Dismiss Notification" : "Cancel Deal"}
                message={deal.status === 'completed' 
                    ? "Are you sure you want to hide this transaction status?" 
                    : "This will cancel the P2P transaction and return the locked funds to the buyer. This action cannot be undone."}
                actionLabel={deal.status === 'completed' ? "Dismiss" : "Cancel Deal"}
                onAction={confirmCancel}
                secondaryLabel="Keep"
            />
        </AnimatePresence>
    );
};
